import { JSX, useContext, useEffect, useMemo } from "react";
import { PageSection, SearchBox } from "@app/shared";
import { CreateExceptionsContext } from "../contexts/CreateExceptionsContext";
import { WizardStepProps } from "../contexts/interfaces";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { Waffles } from "@app/shared/waffles";
import { QESAlert } from "@app/shared/qes-standards";

interface ProjectsTableData {
  [wafflesKeyColumnName]: string;
  projectName: string;
  templateName: string;
  existingExceptions: string;
}

const CreateExceptionsProjects = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(CreateExceptionsContext);

  /* eslint-disable sort-keys */
  const projectsColumnConfig: ColumnConfig = {
    projectName: {
      displayText: "Name",
      testId: "projects",
    },
    templateName: {
      displayText: "Template",
      testId: "template",
    },
    existingExceptions: {
      displayText: "Existing Exceptions",
      testId: "existing-exceptions"
    }
  };
  /* eslint-enable sort-keys */

  useEffect(() => {
    props.pageIsValid(wizardContext.projects.some((project) => project.isChecked));
  }, [wizardContext.projects]);

  const selectedProjectIds: string[] = useMemo(() => {
    return wizardContext.projects.filter((project) => project.isChecked).map((p) => p.customProjectId.toString());
  }, [wizardContext.projects]);

  const projectsTableData: ProjectsTableData[] = useMemo(() => {
    return wizardContext.projects.filter(((project) => project.isVisible)).map<ProjectsTableData>((project) => {
      /* eslint-disable sort-keys */
      return ({
        [wafflesKeyColumnName]: project.customProjectId.toString(),
        projectName: project.projectName,
        templateName: project.templateName,
        existingExceptions: project.hasException ? 'Yes' : 'No',
      });
      /* eslint-enable */
    });
  }, [wizardContext.projects]);

  const handleCheckboxChange = (selectedProjectIds: string[]) => {
    wizardContext.updateSelectedProjects(selectedProjectIds);
  }

  const customSort = (rows: ProjectsTableData[], key: keyof ProjectsTableData, direction: SortDirections) => {
    const directionMultiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (key) {
      case "existingExceptions":
      case "projectName":
      case "templateName":
      default:
        return rows.sort((a, b) => (a[key] > b[key] ? 1 : -1) * directionMultiplier);
    }
  }

  const updateSearchString = (value: string) => {
    wizardContext.updateProjectsVisibility(value);
  }

  return (
    <PageSection>
      <div className="container mt-3">
        <p className="font-weight-bolder">Select the projects you would like to apply your new exceptions to.</p>
        {wizardContext.showChangeWarning && (
          <QESAlert type="warning" persist={true} classNames="mt-3">Changing anything on this page will reset later steps.</QESAlert>
        )}

        <QESAlert type="info" persist={true} classNames="mt-3">
          Any project that has current exceptions will be overwritten if selected. All exceptions must have a project attribution to finish upload.
        </QESAlert>

        {wizardContext.error && (
          <QESAlert type="danger" persist={true} classNames="mt-3">
            {wizardContext.error}
          </QESAlert>
        )}
      </div>

      <div className="container mt-3">
        <SearchBox
          escapeSearchValue={true}
          fieldsetClass="row ml-0 mr-0"
          inputLabel="Search Projects"
          testId="create-exceptions-search-projects"
          onSubmit={(value: string) => updateSearchString(value)}
        />
      </div>

      <div className="container">
        <div className="w-100">
          <Waffles<ProjectsTableData>
            columns={projectsColumnConfig}
            customSort={customSort}
            data={projectsTableData}
            disableProcessingOverlay={true}
            fullWidth={true}
            handleCheckboxChange={handleCheckboxChange}
            selectedKeys={selectedProjectIds}
            showCheckboxes={true}
            testId="create-exceptions-projects-table"
          />
        </div>
      </div>
    </PageSection>
  );
};

export default CreateExceptionsProjects;
